import { useIsUserRole } from "@/app/utils/auth";
import { MagnifyingGlassIcon, PlusCircleIcon } from "@heroicons/react/16/solid";
import { RocketLaunchIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import Header from "../../components/header/Header";
import { IconWithTooltip } from "../../components/iconWithTooltip";
import Modal from "../../components/modal";
import { openModal } from "../../components/modal";
import { useToastEffect } from "../../hooks";
import { useGetCoursesQuery } from "../../services/courseApi";
import CourseCard from "./CourseCard";
import SearchCoursesModal, { SEARCH_MODAL_ID } from "./SearchCoursesModal";
import { CourseInfo, EnrollCourseMask } from "./components/EnrollCourseMask";

export const ENROLL_COURSE_MODAL_ID = "EnrollCourseModal";

function Homepage() {
  const isLecturerOrAdmin = useIsUserRole("lecturer", "admin");
  const navigate = useNavigate();
  const { data, isError, isLoading } = useGetCoursesQuery({});
  const { t } = useTranslation(["homePage"]);
  useToastEffect({ isError, error: t("dataLoadError") });

  const [courseToEnroll, setCourseToEnroll] = useState<CourseInfo>();
  const [resetEnrollMask, setResetEnrollMask] = useState(false);

  useEffect(() => {
    if (resetEnrollMask) {
      setResetEnrollMask(false);
    }
  }, [resetEnrollMask]);

  return (
    <Header showFooter={true}>
      {!isLoading && (
        <div className="flex flex-col items-center">
          <div className="w-full max-w-[800px] lg:w-2/3">
            <div className="mb-sm px-md">
              <div className="flex justify-between">
                <h1 className="font-bold text-2xl sm:text-3xl">
                  {t("myCourses")}
                </h1>
                <div className="flex justify-end gap-x-4 gap-y-4 lg:w-1/2">
                  {isLecturerOrAdmin && (
                    <IconWithTooltip
                      icon={<PlusCircleIcon className="h-8 w-8" />}
                      tooltip={t("newCourse")}
                      handler={() => navigate("course/create")}
                      className="btn btn-ghost rounded-lg"
                    />
                  )}
                  <IconWithTooltip
                    icon={<MagnifyingGlassIcon className="h-8 w-8" />}
                    tooltip={t("tooltipSearchIcon")}
                    handler={() => openModal(SEARCH_MODAL_ID)}
                    className="btn btn-neutral rounded-lg"
                  />
                </div>
              </div>
              <div className="flex min-h-12 flex-col justify-center pt-md">
                {(data ?? []).filter((course) => course.isEnrolled).length >
                0 ? (
                  data
                    ?.filter((course) => course.isEnrolled)
                    ?.sort((a, b) => a.name.localeCompare(b.name))
                    ?.map((course) => (
                      <CourseCard
                        key={course.id}
                        course={course}
                        allCourses={false}
                      />
                    ))
                ) : (
                  <div className="rounded-lg bg-base-200 p-6 text-center shadow-md sm:p-16">
                    <h2 className="mb-4 flex items-center justify-center gap-3 font-bold text-2xl">
                      {t("welcomeNewUser")}{" "}
                      <RocketLaunchIcon className="h-8 w-8" />
                    </h2>
                    <p className="mb-4 whitespace-pre-line text-lg">
                      {t("searchForCoursesMessage")}
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => openModal(SEARCH_MODAL_ID)}
                    >
                      {t("searchForCourses")}{" "}
                      <MagnifyingGlassIcon className="h-7 w-7" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <Modal
            id={ENROLL_COURSE_MODAL_ID}
            title={t("enrollCourseMask")}
            onCancel={() => setResetEnrollMask(true)}
          >
            {courseToEnroll && (
              <EnrollCourseMask
                course={courseToEnroll}
                resetMask={resetEnrollMask}
              />
            )}
          </Modal>
        </div>
      )}
      <SearchCoursesModal
        courses={
          data ? [...data].sort((a, b) => a.name.localeCompare(b.name)) : []
        }
        setCourseToEnroll={setCourseToEnroll}
      />
    </Header>
  );
}

export default Homepage;
